import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addAdjustment,
  fetchAdjustmentLog,
  fetchUserAdjustments,
  updateAdjustment,
  type Adjustment,
  type AdjustmentPatch,
  type AdjustmentWrite,
} from "@/api/access/adjustments";
import { retryQuietlyUnlessReadRefused, retryUnlessReadRefused } from "@/api/access/readRefusal";
import {
  fetchAccessCatalog,
  PermissionsQueryKeys,
} from "@/api/roles-permissions/roles-permissions.queries";
import { refreshPersonRecord } from "@/hooks/user-management/personRecordQueryKeys";

export const UserAdjustmentsQueryKeys = {
  list: "userAdjustments",
  log: "userAdjustmentLog",
} as const;

const CATALOG_STALE_TIME = 5 * 60 * 1000;

/** One adjustment's change log, read only while it is open in the Reactivate modal. */
export const useAdjustmentLog = (adjustment: Adjustment | null) =>
  useQuery({
    queryKey: [UserAdjustmentsQueryKeys.log, adjustment?.id],
    queryFn: ({ signal }) => fetchAdjustmentLog(adjustment!, signal),
    enabled: adjustment !== null,
    retry: false,
  });

export const useUserAdjustments = (userId: string) =>
  useQuery({
    queryKey: [UserAdjustmentsQueryKeys.list, userId],
    queryFn: ({ signal }) => fetchUserAdjustments(userId, signal),
    retry: false,
  });

/**
 * The raw catalog, shared with the role Access tab's read: every key and the actions it offers.
 * `quiet` for a reader that never shows it (the tab strip's count, SERV-3593): a failure of a read
 * it starts raises no toast. A fetch takes the options of the reader that starts it.
 */
export const useAccessCatalogEntries = (enabled: boolean, { quiet = false } = {}) =>
  useQuery({
    queryKey: [PermissionsQueryKeys.getAccessCatalog],
    queryFn: fetchAccessCatalog,
    staleTime: CATALOG_STALE_TIME,
    enabled,
    retry: quiet ? retryQuietlyUnlessReadRefused : retryUnlessReadRefused,
    select: (response) => response.data,
  });

export const useAddAdjustment = (userId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (write: AdjustmentWrite) => addAdjustment(userId, write),
    onSuccess: () => refreshPersonRecord(queryClient, userId, "adjustment"),
  });
};

export const useUpdateAdjustment = (userId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ adjustment, patch }: { adjustment: Adjustment; patch: AdjustmentPatch }) =>
      updateAdjustment(userId, adjustment, patch),
    onSuccess: () => refreshPersonRecord(queryClient, userId, "adjustment"),
  });
};
